import { createMemo, type Accessor } from "solid-js";
import { useNavigate } from "@solidjs/router";

import { useComponentConfig } from "../../lib/hooks/use-component-config";
import {
  useDashboardDateRange,
  appendDateRangeParams,
} from "./DashboardDateRangeContext";
import { useDimensionTypeName } from "../../lib/hooks/use-dimension-type-name";
import { ROUTES, topicDetailRoute, overviewRoute } from "../../lib/config";
import {
  FEATURE_GLOBAL_FILTERS,
  FEATURE_OVERVIEW_PAGE,
  FEATURE_TOPIC_PAGE,
  isFeatureEnabled,
} from "../../lib/feature-flags";
import type {
  DimensionTypeOverviewConfig,
  DimensionTypeOverviewData,
  DimensionRecordBar,
} from "../../lib/types";

export type DimensionTypeOverviewComponentKey =
  | "dimension-type-overview"
  | "dimension-type-overview-strategy";

export type DimensionTypeOverviewHeading =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "ready"; title: string; subheading: string };

export type DimensionTypeOverviewModelOptions = {
  dataSourceOverride?: Accessor<readonly string[] | undefined>;
  enabled?: Accessor<boolean>;
};

export function useDimensionTypeOverviewModel(
  componentKey: DimensionTypeOverviewComponentKey,
  options: DimensionTypeOverviewModelOptions = {},
) {
  const { dateRange } = useDashboardDateRange();
  const dateRangeOverride = createMemo(() => {
    const r = dateRange();
    return r ? { start: r.start, end: r.end } : undefined;
  });

  const component = useComponentConfig<
    DimensionTypeOverviewConfig,
    DimensionTypeOverviewData
  >(componentKey, {
    dateRangeOverride,
    dataSourceOverride: options.dataSourceOverride,
    enabled: options.enabled,
  });

  const navigate = useNavigate();

  // When both the global filters and the topic-detail page are rolled out, an
  // item click drills straight into the topic's dedicated page. Otherwise it
  // falls back to the Themes & Topics page with the type/record filters applied.
  const globalFiltersEnabled = isFeatureEnabled(FEATURE_GLOBAL_FILTERS);
  const topicPageEnabled = isFeatureEnabled(FEATURE_TOPIC_PAGE);
  const topicDetailNavigationEnabled = createMemo(
    () => globalFiltersEnabled() && topicPageEnabled(),
  );

  // Only the non-strategy widget deep-links to /overview, which renders that config.
  const overviewPageEnabled = isFeatureEnabled(FEATURE_OVERVIEW_PAGE);
  const overviewNavigationEnabled = createMemo(
    () => componentKey === "dimension-type-overview" && overviewPageEnabled(),
  );

  const data = createMemo(() => {
    const payload = component.data();
    if (!payload) return payload;
    const records = [...payload.records].sort((left, right) => {
      const leftOrder = left.order;
      const rightOrder = right.order;
      if (typeof leftOrder === "number" && typeof rightOrder === "number") {
        return leftOrder - rightOrder;
      }
      if (typeof leftOrder === "number") return -1;
      if (typeof rightOrder === "number") return 1;
      return 0;
    });
    return { ...payload, records };
  });
  const config = createMemo(() => component.config());
  const isLoading = createMemo(() => component.loading);
  const hasData = createMemo(() => {
    const d = data();
    return d != null && d.records.length > 0;
  });

  const dimensionTypeName = useDimensionTypeName(
    () => config()?.dimensionTypeId,
  );

  // The heading names the configured dimension type. While it (or the config
  // naming it) loads the heading is a loading bar, and a failed fetch or a
  // configured type that resolves no name is an error with a retry, so none of
  // them shows a default heading in its place. Only an overview with no type
  // configured shows the default heading.
  const heading = createMemo<DimensionTypeOverviewHeading>(() => {
    if (dimensionTypeName.failed()) return { state: "failed" };
    const name = dimensionTypeName.name();
    if (name) {
      return {
        state: "ready",
        title: name,
        subheading: `What's the sentiment across our ${name}`,
      };
    }
    if (dimensionTypeName.loading() || (config() === undefined && isLoading())) {
      return { state: "loading" };
    }
    if (config()?.dimensionTypeId) return { state: "failed" };
    return {
      state: "ready",
      title: "Dimension Type Overview",
      subheading: "What's the sentiment across our dimensions",
    };
  });

  const handleExplore = () => {
    const cfg = config();
    if (!cfg?.dimensionTypeId) return;
    const params = new URLSearchParams();
    params.set("types", cfg.dimensionTypeId);
    appendDateRangeParams(params, dateRange());
    navigate(`${ROUTES.topics}?${params.toString()}`);
  };

  const handleRecordClick = (record: DimensionRecordBar) => {
    const cfg = config();
    if (!cfg?.dimensionTypeId) return;
    if (overviewNavigationEnabled()) {
      navigate(overviewRoute(record.dimensionRecordId));
      return;
    }
    if (topicDetailNavigationEnabled()) {
      navigate(topicDetailRoute(record.dimensionRecordId));
      return;
    }
    const params = new URLSearchParams();
    params.set("types", cfg.dimensionTypeId);
    params.set("records", record.dimensionRecordId);
    appendDateRangeParams(params, dateRange());
    navigate(`${ROUTES.topics}?${params.toString()}`);
  };

  const maxRecordCount = createMemo(() => {
    const records = data()?.records ?? [];
    return records.reduce((max, r) => Math.max(max, r.recordCount), 0);
  });

  return {
    data,
    config,
    isLoading,
    hasData,
    heading,
    retryHeading: dimensionTypeName.retry,
    handleExplore,
    handleRecordClick,
    maxRecordCount,
  };
}
